import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { getAllTablesByAreaId, transferTable } from "../../../../../../config/Services";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import "./TransferTableModal.css";

const AREAS_STORAGE_KEY = "restaurant_areas";

const TransferTableModal = ({ open, onClose, onSuccess, currentTable }) => {
  const showSnackbar = useSnackbar();

  const locId = localStorage.getItem("locId");
  const cartId = localStorage.getItem("CartId");
  const retailUserLocalStorageData = JSON.parse(
    localStorage.getItem("retailUserDetails") || "{}"
  );
  const customerDetails = (() => {
    try {
      return JSON.parse(localStorage.getItem("customer_details") || "{}");
    } catch {
      return {};
    }
  })();

  const [allAreas, setAllAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [transferLoader, setTransferLoader] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSelectedTable(null);
    setTables([]);

    const storedAreas = (() => {
      try {
        return JSON.parse(localStorage.getItem(AREAS_STORAGE_KEY) || "{}");
      } catch {
        return {};
      }
    })();

    const areas = Object.values(storedAreas).map((area) => ({
      areaId: area.mapId,
      areaName: area.entityName,
      floorName: area.floorName,
    }));
    setAllAreas(areas);

    const defaultAreaId = currentTable?.layoutId;
    const defaultArea = areas.find((a) => a.areaId === defaultAreaId) || areas[0];

    if (defaultArea) {
      setSelectedArea(defaultArea);
      fetchTables(defaultArea.areaId);
    }
  }, [open]);

  const fetchTables = (areaId) => {
    setTablesLoading(true);
    getAllTablesByAreaId({ parentId: areaId })
      .then((res) => setTables(res?.data || []))
      .catch(() => showSnackbar("Failed to load tables", "error"))
      .finally(() => setTablesLoading(false));
  };

  const handleAreaSelect = (area) => {
    setSelectedArea(area);
    setSelectedTable(null);
    fetchTables(area.areaId);
  };

  const handleTransfer = async () => {
    if (!selectedTable) return;
    setTransferLoader(true);
    const payload = {
      cartId,
      locationId: locId,
      loggedInUser: retailUserLocalStorageData?.uidx || null,
      tableId: selectedTable.metadata?.tableMetadataId || selectedTable.mapId,
      tableNumber: selectedTable.metadata?.tableName || selectedTable.name,
      totalMembers: selectedTable.metadata?.capacity || null,
      layoutId: selectedArea?.areaId,
      layoutName: selectedArea?.areaName,
      userName: customerDetails?.name || null,
      userId: customerDetails?.uidx || null,
      mobileNo: customerDetails?.mobileNumber || null,
      enableWhatsapp: customerDetails?.whatsapp ?? false,
      updatedById: retailUserLocalStorageData?.uidx || null,
      updatedByName: retailUserLocalStorageData?.firstName || null,
      comments: "",
    };
    try {
      await transferTable(payload);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || err?.message || "Transfer failed",
        "error"
      );
      setTransferLoader(false);
      return;
    }
    setTransferLoader(false);

    const transferredTableName = selectedTable.metadata?.tableName || selectedTable.name;
    showSnackbar(`Table transferred to ${transferredTableName}`, "success");
    onSuccess?.({
      tableName: transferredTableName,
      tableId: selectedTable.metadata?.tableMetadataId || selectedTable.mapId,
      layoutId: selectedArea?.areaId,
      layoutName: selectedArea?.areaName,
    });
    onClose();
  };

  const availableTables = tables.filter(
    (t) => t?.status === "AVAILABLE" && t?.mapId !== currentTable?.mapId
  );

  if (!open) return null;

  return (
    <>
      <div className="ttd-overlay" onClick={onClose} />
      <div className="ttd-drawer">

        {/* Header */}
        <div className="ttd-header">
          <div className="ttd-header-top">
            <div className="ttd-title-group">
              <ArrowsRightLeftIcon style={{ width: 20, height: 20, color: "#0860E6" }} />
              <div>
                <p className="ttd-title">Transfer Table</p>
                <p className="ttd-subtitle">Select an available table to transfer to</p>
              </div>
            </div>
            <button className="ttd-close-btn" onClick={onClose}>
              <XMarkIcon style={{ width: 22, height: 22 }} />
            </button>
          </div>

          {/* Area tabs */}
          {allAreas.length > 0 && (
            <div className="ttd-tabs">
              {allAreas.map((area) => (
                <button
                  key={area.areaId}
                  className={`ttd-tab ${selectedArea?.areaId === area.areaId ? "active" : ""}`}
                  onClick={() => handleAreaSelect(area)}
                >
                  {area.areaName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="ttd-body">
          {tablesLoading ? (
            <div className="ttd-loader">
              <CircularProgress size={28} sx={{ color: "#0860E6" }} />
            </div>
          ) : availableTables.length === 0 ? (
            <p className="ttd-empty">No available tables in this area.</p>
          ) : (
            <div className="ttd-grid">
              {availableTables.map((table) => (
                <button
                  key={table.mapId}
                  className={`ttd-table-card ${selectedTable?.mapId === table.mapId ? "selected" : ""}`}
                  onClick={() => setSelectedTable(table)}
                >
                  <span className="ttd-table-name">
                    {table?.metadata?.tableName || table.name}
                  </span>
                  {table?.metadata?.capacity && (
                    <span className="ttd-table-seats">{table.metadata.capacity} seats</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ttd-footer">
          <button className="ttd-cancel-btn" onClick={onClose}>
            <XMarkIcon style={{ width: 16, height: 16 }} />
            Cancel
          </button>
          <button
            className="ttd-transfer-btn"
            onClick={handleTransfer}
            disabled={!selectedTable || transferLoader}
          >
            {transferLoader ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <>
                Transfer Table
                <ArrowsRightLeftIcon style={{ width: 16, height: 16 }} />
              </>
            )}
          </button>
        </div>

      </div>
    </>
  );
};

export default TransferTableModal;
